import { Upload as UploadIcon } from "lucide-react";

interface ProblemsSolutionsPreviewProps {
  settings: {
    title: string;
    subtitle: string;
    centerImage: string;
    problems: string[];
    solutions: string[];
    problemsColor: string;
    problemsTextColor: string;
    problemsIcon: string;
    solutionsColor: string;
    solutionsTextColor: string;
    solutionsIcon: string;
    titleFontSize: string;
    itemFontSize: string;
    padding: string;
  };
}

export const ProblemsSolutionsPreview = ({ settings }: ProblemsSolutionsPreviewProps) => {
  const {
    title,
    subtitle,
    centerImage,
    problems,
    solutions,
    problemsColor,
    problemsTextColor,
    problemsIcon,
    solutionsColor,
    solutionsTextColor,
    solutionsIcon,
    titleFontSize,
    itemFontSize,
    padding,
  } = settings;

  return (
    <div className="py-8" style={{ padding: `${padding}px` }}>
      <div className="max-w-6xl mx-auto">
        <h2
          className="font-bold text-center mb-3"
          style={{ fontSize: `${titleFontSize}px`, lineHeight: "1.2" }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-center text-muted-foreground mb-8 text-lg">{subtitle}</p>
        )}

        {/* Desktop Layout - 3 columns */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 items-start">
          {/* Problems */}
          <div className="space-y-3">
            <h3
              className="font-bold text-center mb-4 text-xl"
              style={{ color: problemsTextColor }}
            >
              المشاكل الشائعة {problemsIcon}
            </h3>
            {problems.map((problem, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg shadow-sm animate-fade-in"
                style={{
                  backgroundColor: problemsColor,
                  color: problemsTextColor,
                  fontSize: `${itemFontSize}px`,
                  animationDelay: `${idx * 0.1}s`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl flex-shrink-0">{problemsIcon}</span>
                  <span>{problem}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Center Image */}
          <div className="flex items-center justify-center">
            {centerImage ? (
              <img
                src={centerImage}
                alt="Product"
                className="max-w-full h-auto rounded-lg shadow-lg animate-scale-in"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full aspect-square bg-muted rounded-lg flex flex-col items-center justify-center">
                <UploadIcon className="h-12 w-12 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">ارفع صورة المنتج</p>
              </div>
            )}
          </div>

          {/* Solutions */}
          <div className="space-y-3">
            <h3
              className="font-bold text-center mb-4 text-xl"
              style={{ color: solutionsTextColor }}
            >
              الحلول المضمونة {solutionsIcon}
            </h3>
            {solutions.map((solution, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg shadow-sm animate-fade-in"
                style={{
                  backgroundColor: solutionsColor,
                  color: solutionsTextColor,
                  fontSize: `${itemFontSize}px`,
                  animationDelay: `${idx * 0.1}s`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl flex-shrink-0">{solutionsIcon}</span>
                  <span>{solution}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Layout - Problems -> Image -> Solutions */}
        <div className="md:hidden space-y-6">
          {/* Problems First on Mobile */}
          <div className="space-y-3">
            <h3
              className="font-bold text-center mb-4 text-lg"
              style={{ color: problemsTextColor }}
            >
              المشاكل الشائعة {problemsIcon}
            </h3>
            {problems.map((problem, idx) => (
              <div
                key={idx}
                className="p-3 rounded-lg shadow-sm animate-fade-in"
                style={{
                  backgroundColor: problemsColor,
                  color: problemsTextColor,
                  fontSize: `${itemFontSize}px`,
                  animationDelay: `${idx * 0.1}s`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl flex-shrink-0">{problemsIcon}</span>
                  <span className="text-sm">{problem}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Image Second on Mobile */}
          <div className="flex items-center justify-center">
            {centerImage ? (
              <img
                src={centerImage}
                alt="Product"
                className="max-w-full h-auto rounded-lg shadow-lg animate-scale-in"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full aspect-square bg-muted rounded-lg flex flex-col items-center justify-center">
                <UploadIcon className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">ارفع صورة المنتج</p>
              </div>
            )}
          </div>

          {/* Solutions Last on Mobile */}
          <div className="space-y-3">
            <h3
              className="font-bold text-center mb-4 text-lg"
              style={{ color: solutionsTextColor }}
            >
              الحلول المضمونة {solutionsIcon}
            </h3>
            {solutions.map((solution, idx) => (
              <div
                key={idx}
                className="p-3 rounded-lg shadow-sm animate-fade-in"
                style={{
                  backgroundColor: solutionsColor,
                  color: solutionsTextColor,
                  fontSize: `${itemFontSize}px`,
                  animationDelay: `${idx * 0.1}s`
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl flex-shrink-0">{solutionsIcon}</span>
                  <span className="text-sm">{solution}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
